import React from 'react';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import { OrgOrgUnitSelectors } from '../../shared/components/inputs/OrgOrgUnitSelectors';
import ApolloError from '../../shared/components/ApolloError';
import PageInstructions from '../../shared/components/PageInstructions';
import useMddapPracticeAreas from '../../shared/hooks/useMddapPracticeAreas';
import GET_MAXIMUM_PROGRAM_YEAR from '../../appraisalView/queries/getMaximumProgramYear';
import { SelectionListMultiSelect } from '../../shared/components/SelectionListMultiSelect';
import GET_ORGS_AND_UNITS_WITH_APPROVED_MDDAP_APPRAISAL from '../queries/getOrgsAndUnitWithApprovedMddapAppraisalQuery';
import { DATA_DOWNLOAD_TYPES } from '../../shared/constants/DataDownloadTypes';
import useDataDownload from '../hooks/useDataDownload';

const maxPracticeGroupLevel = 5;
const pageTitle = 'Data Download';
const pageDescription =
  'Use the download button to access all appraisal data in the Portal. ' +
  'Limit the data to a subsection by using the filters below.';

export const AppraisalDataDownload = () => {
  const { selections, handleSelections, handleDownloadClick, fileDownloadError } = useDataDownload(
    DATA_DOWNLOAD_TYPES.appraisal
  );

  const {
    data: orgsAndUnitsData,
    loading: orgsAndUnitsLoading,
    error: orgsAndUnitsError
  } = useQuery(GET_ORGS_AND_UNITS_WITH_APPROVED_MDDAP_APPRAISAL);

  const {
    data: maxProgramYearData,
    loading: maxProgramYearLoading,
    error: maxProgramYearError
  } = useQuery(GET_MAXIMUM_PROGRAM_YEAR);

  const {
    data: practiceAreaData,
    loading: practiceAreasLoading,
    error: practiceAreasError
  } = useMddapPracticeAreas();

  const organizations = orgsAndUnitsData
    ? orgsAndUnitsData.orgsAndUnitsWithApprovedMddapAppraisal
    : [];
  const programYearOptions = maxProgramYearData
    ? getOptionsByMaxNumber(maxProgramYearData.maximumProgramYear, mapProgramYearToOption)
    : [];
  const practiceAreaOptions = getPracticeAreaOptions(practiceAreaData);
  const practiceGroupLevelOptions = getOptionsByMaxNumber(
    maxPracticeGroupLevel,
    mapPracticeGroupToOption
  );

  if (orgsAndUnitsError || practiceAreasError || maxProgramYearError || fileDownloadError) {
    return <ApolloError />;
  }

  return (
    <>
      <section className='page-instructions'>
        <PageInstructions title={pageTitle} description={pageDescription} />
        <Button className='primary-button' onClick={handleDownloadClick}>
          Download Report
        </Button>
      </section>
      <div className='data-download'>
        <section className='page-section'>
          <OrgOrgUnitSelectors
            sectionClassName='option-selector'
            organizations={organizations}
            orgSelectorProps={{
              value: selections.orgIds,
              onChange: handleSelections('orgIds'),
              label: 'Organization Name(s)'
            }}
            orgUnitSelectorProps={{
              value: selections.orgUnitIds,
              onChange: handleSelections('orgUnitIds'),
              label: 'OU Name(s)'
            }}
            loading={orgsAndUnitsLoading}
          />
          <div className='option-selector'>
            <SelectionListMultiSelect
              id='program-year-selector'
              label='Program Year(s)'
              placeholder='Select Program Year(s)'
              options={programYearOptions}
              value={selections.programYears}
              onChange={handleSelections('programYears')}
              loading={maxProgramYearLoading}
            />
          </div>
        </section>
        <section className='page-section'>
          <div className='option-selector'>
            <SelectionListMultiSelect
              id='practice-area-selector'
              label='Practice Area(s)'
              placeholder='Select Practice Area(s)'
              options={practiceAreaOptions}
              value={selections.practiceAreas}
              onChange={handleSelections('practiceAreas')}
              loading={practiceAreasLoading}
            />
          </div>
          <div className='option-selector'>
            <SelectionListMultiSelect
              id='practice-group-selector'
              label='Practice Group Level(s)'
              placeholder='Select Practice Group Level(s)'
              options={practiceGroupLevelOptions}
              value={selections.practiceGroupLevels}
              onChange={handleSelections('practiceGroupLevels')}
            />
          </div>
        </section>
      </div>
    </>
  );
};

const getPracticeAreaOptions = practiceAreas =>
  _.chain(practiceAreas)
    .orderBy(opt => opt.label)
    .map(mapPracticeAreaToOptions)
    .value();

const getOptionsByMaxNumber = (maxNumber, labelFormatter) =>
  _.range(1, maxNumber + 1).map(labelFormatter);

const mapPracticeGroupToOption = practiceGroupLevel => ({
  label: `Level ${practiceGroupLevel}`,
  value: practiceGroupLevel
});

const mapProgramYearToOption = programYear => ({
  label: `Y${programYear}`,
  value: programYear
});

const mapPracticeAreaToOptions = pa => ({
  label: pa.Name,
  value: pa.InternalId
});
